import { deleteItem, getItem, setItem } from '../../../utils/storage'

const SESSION_FINISH_GUARD_PREFIX = 'session_finish_guard'
const SESSION_FINISH_GUARD_MAX_AGE_MS = 10 * 60 * 1000

interface SessionFinishGuardPayload {
  sessionId: string
  createdAtMs: number
}

const sanitizeKeyPart = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9._-]/g, '_')
}

const isValidPayload = (value: unknown): value is SessionFinishGuardPayload => {
  if (typeof value !== 'object' || value == null) return false

  const payload = value as Record<string, unknown>
  return (
    typeof payload.sessionId === 'string' &&
    payload.sessionId.length > 0 &&
    typeof payload.createdAtMs === 'number' &&
    Number.isFinite(payload.createdAtMs)
  )
}

export const buildSessionFinishGuardKey = (sessionId: string): string => {
  return `${SESSION_FINISH_GUARD_PREFIX}_${sanitizeKeyPart(sessionId)}`
}

export const markSessionFinishEligible = async ({
  sessionId,
  now = Date.now(),
}: {
  sessionId: string
  now?: number
}): Promise<boolean> => {
  if (sessionId.trim().length === 0 || !Number.isFinite(now)) {
    return false
  }

  const payload: SessionFinishGuardPayload = {
    sessionId,
    createdAtMs: now,
  }

  try {
    await setItem(
      buildSessionFinishGuardKey(sessionId),
      JSON.stringify(payload),
    )
    return true
  } catch (error) {
    console.warn('Failed to persist session finish guard', error)
    return false
  }
}

export const consumeSessionFinishEligibility = async ({
  sessionId,
  now = Date.now(),
  maxAgeMs = SESSION_FINISH_GUARD_MAX_AGE_MS,
}: {
  sessionId: string
  now?: number
  maxAgeMs?: number
}): Promise<boolean> => {
  if (
    sessionId.trim().length === 0 ||
    !Number.isFinite(now) ||
    !Number.isFinite(maxAgeMs) ||
    maxAgeMs <= 0
  ) {
    return false
  }

  const key = buildSessionFinishGuardKey(sessionId)
  let storedPayload: string | null = null

  try {
    storedPayload = await getItem(key)
  } catch (error) {
    console.warn('Failed to read session finish guard', error)
    return false
  }

  if (storedPayload == null) {
    return false
  }

  let parsedPayload: unknown = null
  try {
    parsedPayload = JSON.parse(storedPayload)
  } catch {
    parsedPayload = null
  }

  try {
    await deleteItem(key)
  } catch (error) {
    console.warn('Failed to clear session finish guard', error)
  }

  if (!isValidPayload(parsedPayload)) {
    return false
  }

  if (parsedPayload.sessionId !== sessionId) {
    return false
  }

  const guardAge = now - parsedPayload.createdAtMs
  if (guardAge < 0 || guardAge > maxAgeMs) {
    return false
  }

  return true
}
